import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useMerchantRequest, useMerchantRequests } from "@/api/hooks/admin";
import {
  useArchiveMerchantRequest,
  useDeclineMerchantRequest,
  usePublishMerchantRequest,
  useRunRoleIntake,
  useUpdateMerchantRequest,
} from "@/api/hooks/mutations";
import type { AdminMerchantRequestSummary } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { buttonClass, Button } from "@/components/ui/Button";
import { Field, fieldInputClass } from "@/components/ui/Field";
import { ChipInput } from "@/components/ui/ChipInput";
import { PaginationControls } from "@/components/ui/PaginationControls";
import { PillTabs } from "@/components/ui/PillTabs";
import { StatusTag } from "@/components/ui/StatusTag";
import { fmtDate, humanize, parseBudgetRange } from "@/lib/format";
import { safeUrl } from "@/lib/safe-url";

const STATUS_OPTIONS = [
  { value: "all" as const, label: "All" },
  { value: "new" as const, label: "New" },
  { value: "in_review" as const, label: "In review" },
  { value: "published" as const, label: "Published" },
  { value: "declined" as const, label: "Declined" },
  { value: "archived" as const, label: "Archived" },
];

type StatusValue = (typeof STATUS_OPTIONS)[number]["value"];

export function AdminRequestsPage() {
  const { id } = useParams();
  const detailId = id ? parseInt(id, 10) : null;
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const status = (searchParams.get("status") as StatusValue) ?? "all";
  const q = searchParams.get("q") ?? "";
  const page = parseInt(searchParams.get("page") ?? "1", 10);
  const PAGE_SIZE = 20;

  const list = useMerchantRequests({ status, q: q || undefined, page, page_size: PAGE_SIZE });

  const [searchDraft, setSearchDraft] = useState(q);
  useEffect(() => setSearchDraft(q), [q]);
  const debounceRef = useRef<number | null>(null);
  function onSearch(v: string) {
    setSearchDraft(v);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      next.delete("page");
      if (v) next.set("q", v);
      else next.delete("q");
      setSearchParams(next, { replace: true });
    }, 300);
  }

  function setStatus(s: StatusValue) {
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    if (s === "all") next.delete("status");
    else next.set("status", s);
    setSearchParams(next, { replace: true });
  }

  function setPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Merchant requests</h1>
          <p className="mt-1 text-sm text-text-muted">
            {list.isLoading ? "Loading…" : `${list.data?.total ?? 0} total`}
          </p>
        </div>
        <input
          type="text"
          value={searchDraft}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search name, email, company, position…"
          className="w-full rounded-md border border-border bg-surface px-3.5 py-2 text-sm outline-none focus:border-primary sm:w-72"
        />
      </div>

      <PillTabs value={status} onChange={setStatus} options={STATUS_OPTIONS} />

      <div className="flex flex-col gap-3 md:hidden">
        {list.isLoading ? (
          <MobileEmptyState>Loading…</MobileEmptyState>
        ) : !list.data?.items.length ? (
          <MobileEmptyState>No requests match.</MobileEmptyState>
        ) : (
          list.data.items.map((r) => <RequestCard key={r.id} row={r} />)
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-2xl border border-border bg-surface md:block">
        <table className="min-w-[760px] w-full border-separate border-spacing-0 text-[13px]">
          <thead>
            <tr className="bg-surface-alt text-left text-[12px] font-semibold uppercase tracking-wider text-text-muted">
              <th className="px-4 py-3.5">Merchant</th>
              <th className="px-4 py-3.5">Position</th>
              <th className="px-4 py-3.5">Type</th>
              <th className="px-4 py-3.5">Contact</th>
              <th className="px-4 py-3.5">Status</th>
              <th className="px-4 py-3.5">Submitted</th>
              <th className="px-4 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-text-muted">
                  Loading…
                </td>
              </tr>
            ) : !list.data?.items.length ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-text-muted">
                  No requests match.
                </td>
              </tr>
            ) : (
              list.data.items.map((r) => <RequestRow key={r.id} row={r} />)
            )}
          </tbody>
        </table>
      </div>

      {list.data && list.data.total > list.data.page_size ? (
        <PaginationControls
          page={list.data.page}
          pageSize={list.data.page_size}
          total={list.data.total}
          itemLabel="requests"
          onPageChange={setPage}
        />
      ) : null}

      {detailId != null && (
        <DetailModal label="Merchant request detail" onClose={() => navigate("/admin/requests")}>
          <RequestDetail id={detailId} />
        </DetailModal>
      )}
    </div>
  );
}

function RequestRow({ row }: { row: AdminMerchantRequestSummary }) {
  return (
    <tr className="border-t border-border hover:bg-surface-alt">
      <td className="px-4 py-3.5 font-medium">{row.contact_company}</td>
      <td className="px-4 py-3.5">
        <div className="font-medium">{row.position_title}</div>
        <div className="text-text-muted">
          {humanize(row.seniority, "seniority")} · {row.budget || "—"}
        </div>
      </td>
      <td className="px-4 py-3.5">
        <span className="rounded-full bg-surface-alt px-2 py-0.5 text-[11px] font-medium">
          {humanize(row.service_type, "service")}
        </span>
      </td>
      <td className="px-4 py-3.5">
        <div className="font-medium">{row.contact_name}</div>
        <div className="text-text-muted">{row.contact_email}</div>
      </td>
      <td className="px-4 py-3.5">
        <StatusTag status={row.status} />
      </td>
      <td className="px-4 py-3.5 text-text-muted">{fmtDate(row.created_at)}</td>
      <td className="px-4 py-3.5">
        <Link
          to={`/admin/requests/${row.id}`}
          className={buttonClass({ variant: "ghost", size: "sm" })}
        >
          Review →
        </Link>
      </td>
    </tr>
  );
}

function RequestCard({ row }: { row: AdminMerchantRequestSummary }) {
  return (
    <article className="rounded-2xl border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold">{row.contact_company}</div>
          <div className="mt-1 text-sm text-text-muted">{row.position_title}</div>
        </div>
        <StatusTag status={row.status} />
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-text-muted">
        <div>{humanize(row.service_type, "service")} · {humanize(row.seniority, "seniority")}</div>
        <div>{row.contact_name} · {row.contact_email}</div>
        <div>{row.budget || "Budget not specified"}</div>
      </div>
      <Link
        to={`/admin/requests/${row.id}`}
        className={`${buttonClass({ variant: "ghost", size: "sm" })} mt-4 w-full justify-center`}
      >
        Review
      </Link>
    </article>
  );
}

function RequestDetail({ id }: { id: number }) {
  const navigate = useNavigate();
  const detail = useMerchantRequest(id);
  const update = useUpdateMerchantRequest();
  const publish = usePublishMerchantRequest();
  const decline = useDeclineMerchantRequest();
  const runIntake = useRunRoleIntake();
  const archive = useArchiveMerchantRequest();

  // Form state — fully local. Sync from server data on first load / id change.
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [confirmAction, setConfirmAction] = useState<CleanupAction>(null);
  const [declineReason, setDeclineReason] = useState("");

  useEffect(() => {
    if (!detail.data) return;
    const r = detail.data;
    const range = parseBudgetRange(r.budget);
    setDraft({
      service_type: r.service_type,
      contact_name: r.contact_name,
      contact_email: r.contact_email,
      contact_phone: r.contact_phone ?? "",
      contact_company: r.contact_company,
      position_title: r.position_title,
      seniority: r.seniority,
      engagement: r.engagement,
      location: r.location ?? "",
      budget: r.budget ?? "",
      currency: "USD",
      salary_min: range.min ?? "",
      salary_max: range.max ?? "",
      description: r.description,
      admin_notes: r.admin_notes ?? "",
      skills: r.skills ?? [],
    });
  }, [detail.data, id]);

  if (detail.isLoading) {
    return (
      <section className="mt-6 rounded-2xl border border-border bg-surface p-6 text-text-muted">
        Loading detail…
      </section>
    );
  }
  if (detail.error) {
    return (
      <section className="mt-6 rounded-2xl border border-border bg-surface p-6 text-danger">
        {detail.error instanceof ApiError && detail.error.status === 404
          ? "This request no longer exists."
          : detail.error instanceof Error
            ? detail.error.message
            : "Could not load this request."}
      </section>
    );
  }
  if (!detail.data) {
    return (
      <section className="mt-6 rounded-2xl border border-border bg-surface p-6 text-text-muted">
        This request is unavailable.
      </section>
    );
  }
  if (!draft) {
    return (
      <section className="mt-6 rounded-2xl border border-border bg-surface p-6 text-text-muted">
        Loading detail…
      </section>
    );
  }

  const r = detail.data;

  function set<K extends keyof DraftState>(k: K, v: DraftState[K]) {
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  }

  async function onSave() {
    if (!draft) return;
    try {
      await update.mutateAsync({
        id,
        body: {
          service_type: draft.service_type as "outstaffing" | "recruiting",
          contact_name: draft.contact_name,
          contact_email: draft.contact_email,
          contact_phone: draft.contact_phone || null,
          contact_company: draft.contact_company,
          position_title: draft.position_title,
          seniority: draft.seniority as "junior" | "middle" | "senior" | "lead",
          engagement: draft.engagement as "full_time" | "part_time" | "project_based",
          skills: draft.skills,
          location: draft.location || null,
          budget: draft.budget || null,
          description: draft.description,
          admin_notes: draft.admin_notes || null,
        },
      });
      toast.success("Saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Save failed");
    }
  }

  async function onPublish() {
    if (!draft) return;
    const wasPublished = r.status === "published";
    try {
      const min = toIntOrUndef(draft.salary_min);
      const max = toIntOrUndef(draft.salary_max);
      await publish.mutateAsync({
        id,
        body: {
          title: draft.position_title || null,
          company_name: draft.contact_company || null,
          seniority: draft.seniority as "junior" | "middle" | "senior" | "lead",
          engagement_type: draft.service_type as "outstaffing" | "recruiting",
          schedule: draft.engagement || null,
          location: draft.location || null,
          salary_min: min ?? null,
          salary_max: max ?? null,
          currency: draft.currency || "USD",
          skills: draft.skills,
          description: draft.description || null,
        },
      });
      toast.success(wasPublished ? "Published position updated" : "Published as a position");
      if (!wasPublished) navigate("/admin/requests");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("This request cannot be published in its current state.");
      } else {
        toast.error(err instanceof ApiError ? err.message : "Publish failed");
      }
    }
  }

  async function onDecline() {
    try {
      await decline.mutateAsync({ id, reason: declineReason.trim() || null });
      toast.success("Declined");
      navigate("/admin/requests");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error("Already in a terminal state.");
      } else {
        toast.error(err instanceof ApiError ? err.message : "Decline failed");
      }
    }
  }

  async function onRunIntake() {
    try {
      const role = await runIntake.mutateAsync(id);
      toast.success("Role intake created");
      navigate(`/admin/role-rail/${role.id}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Role intake failed");
    }
  }

  async function onArchive() {
    try {
      await archive.mutateAsync(id);
      toast.success("Request archived");
      navigate("/admin/requests");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Archive failed");
    }
  }

  const publishButtonLabel =
    r.status === "published" ? "Update published position" : "Publish position";

  return (
    <section className="mt-6 rounded-2xl border border-border bg-surface p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-bold">
            Request: {r.position_title} — {r.contact_company}
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            Submitted {fmtDate(r.created_at)} · <StatusTag status={r.status} />
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button variant="danger" onClick={() => setConfirmAction("decline")} loading={decline.isPending}>
            Decline
          </Button>
          <Button variant="secondary" onClick={() => setConfirmAction("archive")}>
            Archive request
          </Button>
          <Button variant="secondary" onClick={onSave} loading={update.isPending}>
            Save
          </Button>
          <Button variant="secondary" onClick={onRunIntake} loading={runIntake.isPending}>
            Run intake
          </Button>
          <Button onClick={onPublish} loading={publish.isPending}>
            {publishButtonLabel}
          </Button>
          <button
            type="button"
            onClick={() => navigate("/admin/requests")}
            className={`${buttonClass({ variant: "ghost", size: "sm" })} w-full justify-center sm:w-auto`}
          >
            Close
          </button>
        </div>
      </div>

      {confirmAction && (
        confirmAction === "archive" ? (
          <CleanupConfirm
            noun="request"
            itemName={r.position_title}
            loading={archive.isPending}
            onCancel={() => setConfirmAction(null)}
            onConfirm={onArchive}
          />
        ) : (
          <DeclineConfirm
            noun="request"
            itemName={r.position_title}
            value={declineReason}
            loading={decline.isPending}
            onChange={setDeclineReason}
            onCancel={() => {
              setConfirmAction(null);
              setDeclineReason("");
            }}
            onConfirm={onDecline}
          />
        )
      )}

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Service type">
          <select
            value={draft.service_type}
            onChange={(e) => set("service_type", e.target.value)}
            className={fieldInputClass()}
          >
            <option value="outstaffing">Outstaffing</option>
            <option value="recruiting">Recruiting</option>
          </select>
        </Field>
        <Field label="Position title">
          <input
            type="text"
            value={draft.position_title}
            onChange={(e) => set("position_title", e.target.value)}
            className={fieldInputClass()}
          />
        </Field>
        <Field label="Seniority">
          <select
            value={draft.seniority}
            onChange={(e) => set("seniority", e.target.value)}
            className={fieldInputClass()}
          >
            <option value="junior">Junior</option>
            <option value="middle">Middle</option>
            <option value="senior">Senior</option>
            <option value="lead">Lead / Principal</option>
          </select>
        </Field>
        <Field label="Engagement">
          <select
            value={draft.engagement}
            onChange={(e) => set("engagement", e.target.value)}
            className={fieldInputClass()}
          >
            <option value="full_time">Full-time</option>
            <option value="part_time">Part-time</option>
            <option value="project_based">Project-based</option>
          </select>
        </Field>
        <Field label="Location">
          <input
            type="text"
            value={draft.location}
            onChange={(e) => set("location", e.target.value)}
            className={fieldInputClass()}
          />
        </Field>
        <Field label="Budget (free text)">
          <input
            type="text"
            value={draft.budget}
            onChange={(e) => set("budget", e.target.value)}
            placeholder="e.g. $7,000 – $9,000"
            className={fieldInputClass()}
          />
        </Field>
        <Field label="Currency">
          <input
            type="text"
            value={draft.currency}
            onChange={(e) => set("currency", e.target.value)}
            maxLength={6}
            className={fieldInputClass()}
          />
        </Field>
        <Field label="Salary min (numeric, sent on publish)">
          <input
            type="number"
            value={draft.salary_min}
            onChange={(e) => set("salary_min", e.target.value)}
            placeholder="e.g. 7000"
            className={fieldInputClass()}
          />
        </Field>
        <Field label="Salary max (numeric, sent on publish)">
          <input
            type="number"
            value={draft.salary_max}
            onChange={(e) => set("salary_max", e.target.value)}
            placeholder="e.g. 9000"
            className={fieldInputClass()}
          />
        </Field>
        <Field label="Skills" className="sm:col-span-2">
          <ChipInput ariaLabel="Skills" value={draft.skills} onChange={(v) => set("skills", v)} />
        </Field>
        <Field label="Description (public when published)" className="sm:col-span-2">
          <p className="mb-2 text-xs leading-5 text-text-muted">
            Keep candidate cost, client price, and margin details in Budget or Internal notes.
          </p>
          <textarea
            value={draft.description}
            onChange={(e) => set("description", e.target.value)}
            className={`${fieldInputClass()} min-h-[120px] resize-y`}
          />
        </Field>
        <Field label="Internal notes" className="sm:col-span-2">
          <textarea
            value={draft.admin_notes}
            onChange={(e) => set("admin_notes", e.target.value)}
            placeholder="Visible only to admins…"
            className={`${fieldInputClass()} min-h-[80px] resize-y`}
          />
        </Field>
      </div>

      <div className="mt-5 rounded-xl border border-border bg-surface-alt p-4 text-sm">
        <h3 className="mb-3 font-semibold">Intake metadata</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {r.published_position_id ? (
            <ReadonlyPair label="Published position" value={`#${r.published_position_id}`} />
          ) : null}
          <ReadonlyPair label="Source channel" value={humanize(r.source_channel)} />
          <ReadonlyPair label="Buyer type" value={humanize(r.buyer_type)} />
          <ReadonlyPair label="Timezone requirement" value={displayValue(r.timezone_requirement)} />
          <ReadonlyPair label="Contract duration" value={displayValue(r.contract_duration)} />
          <ReadonlyPair label="Urgency" value={humanize(r.urgency)} />
          {r.source_campaign ? (
            <ReadonlyPair label="Source campaign" value={r.source_campaign} />
          ) : null}
        </div>
      </div>

      <div className="mt-5 border-t border-border pt-4 text-sm">
        <h3 className="mb-2 font-semibold">Contact</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <span className="text-text-muted">Name: </span>
            {r.contact_name}
          </div>
          <div>
            <span className="text-text-muted">Email: </span>
            {r.contact_email}
          </div>
          <div>
            <span className="text-text-muted">Phone: </span>
            {r.contact_phone ?? "—"}
          </div>
          <div>
            <span className="text-text-muted">Website: </span>
            <UrlValue value={r.contact_website} />
          </div>
        </div>
      </div>
    </section>
  );
}

function displayValue(value: string | null | undefined) {
  return value?.trim() || "—";
}

function ReadonlyPair({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-text-muted">{label}</div>
      <div className="mt-0.5 font-medium">{value}</div>
    </div>
  );
}

function UrlValue({ value }: { value?: string | null }) {
  if (!value) return <>—</>;
  const safe = safeUrl(value);
  if (!safe) return <>{value}</>;
  return (
    <a href={safe} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
      {value}
    </a>
  );
}

function MobileEmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-6 text-center text-sm text-text-muted">
      {children}
    </div>
  );
}

function DetailModal({ children, label, onClose }: { children: React.ReactNode; label: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 px-3 py-4 sm:px-6" role="dialog" aria-modal="true" aria-label={label}>
      <div className="mx-auto flex max-h-full max-w-6xl flex-col rounded-2xl bg-background shadow-2xl">
        <div className="flex items-center justify-end border-b border-border px-4 py-3">
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
        <div className="overflow-y-auto p-4 sm:p-6">
          {children}
        </div>
      </div>
    </div>
  );
}

type CleanupAction = "archive" | "decline" | null;

function CleanupConfirm({
  noun,
  itemName,
  loading,
  onCancel,
  onConfirm,
}: {
  noun: string;
  itemName: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="alertdialog"
      aria-label={`Archive ${noun}`}
      className="mb-5 rounded-xl border border-[#fecaca] bg-danger-soft p-4 text-sm"
    >
      <div className="font-semibold text-[#991b1b]">
        Archive this {noun}?
      </div>
      <p className="mt-1 text-[#7f1d1d]">
        This moves {itemName} out of the active workflow while keeping the record available for audit.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={onConfirm} loading={loading}>
          Confirm archive {noun}
        </Button>
      </div>
    </div>
  );
}

function DeclineConfirm({
  noun,
  itemName,
  value,
  loading,
  onChange,
  onCancel,
  onConfirm,
}: {
  noun: string;
  itemName: string;
  value: string;
  loading: boolean;
  onChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      role="alertdialog"
      aria-label={`Decline ${noun}`}
      className="mb-5 rounded-xl border border-[#fecaca] bg-danger-soft p-4 text-sm"
    >
      <div className="font-semibold text-[#991b1b]">
        Decline this {noun}?
      </div>
      <p className="mt-1 text-[#7f1d1d]">
        Add an optional reason for declining {itemName}. This stays attached to the record for audit.
      </p>
      <label className="mt-3 block">
        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#991b1b]">
          Decline reason
        </span>
        <textarea
          aria-label="Decline reason"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="min-h-[96px] w-full resize-y rounded-md border border-[#fca5a5] bg-white px-3 py-2.5 text-[13px] text-text outline-none focus:border-[#ef4444]"
          placeholder="Optional internal context…"
        />
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={onConfirm} loading={loading}>
          Confirm decline {noun}
        </Button>
      </div>
    </div>
  );
}

type DraftState = {
  service_type: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_company: string;
  position_title: string;
  seniority: string;
  engagement: string;
  location: string;
  budget: string;
  currency: string;
  salary_min: number | string;
  salary_max: number | string;
  description: string;
  admin_notes: string;
  skills: string[];
};

function toIntOrUndef(v: string | number): number | undefined {
  if (v === "" || v === undefined || v === null) return undefined;
  const n = typeof v === "number" ? v : parseInt(v, 10);
  return isNaN(n) ? undefined : n;
}
